import { CurrencyPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription, firstValueFrom } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import Swal from 'sweetalert2';
import { CuentaRetiro } from '../../interfaces/cuenta-retiro.interface';
import { AutomaticRateService } from '../../services/automatic-rate.service';
import { BinanceDepositService } from '../../services/binance-deposit.service';
import { BinanceWithdrawalService, BalanceResponse } from '../../services/binance-withdrawal.service';
import { CartolaService } from '../../services/cartola.service';
import { EstadoTramosUsdtClpResponse, MainRatesService } from '../../services/main-rates.service';
import { WithdrawAccountService } from '../../services/withdraw-account.service';
import { AddWithdrawAccountsComponent } from '../add-withdraw-accounts/add-withdraw-accounts.component';
import {
  clasificarErrorConversionClp,
  cotizarVentaClp,
  TEXTO_SIN_TASA_CLP,
  TEXTO_TASA_CAMBIO_CLP,
  tramosValidosClp,
} from '../calculator-cryto/clp-usdt.tramos';
import { EscalonVista } from '../home-rates/models/home-rates.model';
import { escaleraDesde } from '../home-rates/rules/home-rates.rules';
import { mensajeEnvioIncierto } from '../convert-usdt-ves/convert-usdt-ves.rules';

@Component({
  selector: 'app-convert-usdt-clp',
  templateUrl: './convert-usdt-clp.component.html',
  styleUrls: ['./convert-usdt-clp.component.sass'],
})
export class ConvertUsdtClpComponent implements OnInit, OnDestroy {
  readonly MIN_USDT_AMOUNT = 20;
  userSubscription: Subscription = new Subscription();
  rateSubscription: Subscription = new Subscription();
  balanceUsdtSubscription: Subscription = new Subscription();
  balanceClpSubscription: Subscription = new Subscription();
  rateValues: any = null;
  /** Tasa BASE de venta (`general-types/usdtRateSell`), la de siempre. */
  usdtToClpRate: number | null = null;
  /**
   * Tramos USDT→CLP (22/10/2026), de `rates/tramos-usdt/usdt-clp`. Null si la
   * llamada falló (backend viejo, 404, 503): entonces se paga la base, como
   * antes.
   */
  tramosVenta: EstadoTramosUsdtClpResponse | null = null;
  /** Tasa que se paga por el monto actual: la del tramo o la base. */
  tasaAplicada: number | null = null;
  /** Mejor tasa publicada («Desde X»); null si ningún tramo mejora la base. */
  desdeTasa: number | null = null;
  /** CLP de más frente a la base con el monto actual (0 = no se muestra). */
  extraClp = 0;
  previousRate: number | null = null;
  errorMessage: string | null = null;
  loading: boolean = true;
  saldoUsdt: number = 0;
  saldoClp: number = 0;
  montoConvertido: number = 0;
  montoAConvertir: number = 0;
  user: any;
  horarioOperacion: string = '5:00 - 23:00';
  rateChanged: boolean = false;
  isConverting: boolean = false;
  isOperatingHours: boolean = true;
  timeCheckInterval: any;
  operatingHoursMessage: string =
    'No puedes operar en este momento. El horario de operación es de 5:00 a 23:00 (hora de Chile).';

  // Beneficiarios (solo para usuarios VE)
  isVeUser: boolean = false;
  beneficiaries: CuentaRetiro[] = [];
  selectedBeneficiary: CuentaRetiro | null = null;
  searchTerm: string = '';
  loadingBeneficiarios: boolean = false;

  constructor(
    private automaticRateService: AutomaticRateService,
    private store: Store<AppState>,
    private binanceDepositService: BinanceDepositService,
    private binanceWithdrawalService: BinanceWithdrawalService,
    private cartolaService: CartolaService,
    private withdrawAccountService: WithdrawAccountService,
    private currencyPipe: CurrencyPipe,
    private router: Router,
    private dialog: MatDialog,
    private mainRates: MainRatesService
  ) {}

  ngOnInit(): void {
    this.checkOperatingHours();
    this.setupTimeMonitoring();
    this.userSubscription = this.store
      .select('auth')
      .subscribe(({ user }: any) => {
        this.user = user;
        this.isVeUser = user?.tipoId === 'VE';
        if (user && user.usuarioId) {
          this.initData(user.usuarioId);
          if (this.isVeUser) {
            this.loadBeneficiaries();
          }
        }
      });
    this.setupRateSubscription();
  }

  ngOnDestroy(): void {
    this.rateSubscription.unsubscribe();
    this.balanceUsdtSubscription.unsubscribe();
    this.balanceClpSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
    if (this.timeCheckInterval) {
      clearInterval(this.timeCheckInterval);
    }
  }

  private checkOperatingHours(): void {
    const now = new Date();
    const chileTime = new Date(
      now.toLocaleString('en-US', { timeZone: 'America/Santiago' })
    );
    const currentHour = chileTime.getHours();

    this.isOperatingHours = currentHour >= 5 && currentHour < 23;

    if (!this.isOperatingHours) {
      this.resetConversionValues();
      this.errorMessage = this.operatingHoursMessage;
    } else if (this.errorMessage === this.operatingHoursMessage) {
      this.errorMessage = null;
    }

  }

  private setupTimeMonitoring(): void {
    this.timeCheckInterval = setInterval(() => {
      this.checkOperatingHours();
    }, 60000); // Verificar cada minuto
  }

  // Eliminada función setupSubscriptions, ahora la lógica está en ngOnInit
  private async initData(userId: string) {
    try {
      // Obtener tasa inicial (y los tramos en paralelo: si fallan, la base).
      const [rateValues, tramos] = await Promise.all([
        this.automaticRateService.getUsdtRateSellForUser(),
        this.leerTramosVenta(),
      ]);
      this.rateValues = rateValues;
      this.tramosVenta = tramos;
      if (!this.rateValues?.valoresCambio?.tasaUsdtClp) {
        throw new Error('El valor tasaUsdtClp no está disponible.');
      }
      this.usdtToClpRate = this.rateValues.valoresCambio.tasaUsdtClp;
      if (this.usdtToClpRate) {
      }

      // Obtener saldos iniciales (usar saldo disponible en lugar de total)
      const balanceObservable = await this.binanceWithdrawalService.getBalance();
      const balance: BalanceResponse = await firstValueFrom(balanceObservable);
      this.saldoUsdt = balance.availableBalance || 0;
      const saldoCliente = await this.cartolaService.getSaldoCliente(userId);
      this.saldoClp = saldoCliente?.saldo || 0;

      // Suscripción a balances en tiempo real
      this.setupBalanceSubscription(userId);
      this.loading = false;

      // Validar saldo inicial
      this.calcularConversion();
    } catch (error: any) {
      this.errorMessage = 'Error al obtener valores iniciales.';
      this.rateValues = null;
      this.usdtToClpRate = null;
      this.loading = false;
    }
  }

  /** Tramos USDT→CLP; null si la llamada falla o el servicio no la tiene. Nunca lanza. */
  private async leerTramosVenta(): Promise<EstadoTramosUsdtClpResponse | null> {
    try {
      return (await firstValueFrom(this.mainRates.obtenerTramosUsdtClp())) ?? null;
    } catch {
      return null;
    }
  }

  /** Vuelve a leer la base y los tramos (tras un 400 «La tasa cambió…»). */
  async recotizar(): Promise<void> {
    try {
      const [rateValues, tramos] = await Promise.all([
        this.automaticRateService.getUsdtRateSellForUser(),
        this.leerTramosVenta(),
      ]);
      const base = Number(rateValues?.valoresCambio?.tasaUsdtClp);
      if (base > 0) {
        this.rateValues = rateValues;
        this.usdtToClpRate = base;
      }
      this.tramosVenta = tramos;
    } catch {
      // Se queda con lo que tenía; el back vuelve a validar al confirmar.
    }
    this.calcularConversion();
  }

  private setupRateSubscription(): void {
    this.rateSubscription = this.automaticRateService
      .getRateSellUpdates()
      .subscribe({
        next: (rate: number) => {
          this.previousRate = this.usdtToClpRate;
          this.usdtToClpRate = rate;

          this.rateChanged =
            this.previousRate !== null &&
            this.previousRate !== this.usdtToClpRate;

          if (this.rateChanged) {
            this.resetConversionValues();
            // Base nueva: los tramos también se recalculan en el servidor.
            this.leerTramosVenta().then((t) => (this.tramosVenta = t));
          }
        },
        error: (error: Error) => {
          this.errorMessage = 'Error al obtener actualizaciones de tasa.';
        },
      });
  }

  private setupBalanceSubscription(userId: string): void {
    // Suscripción al balance de USDT disponible (no el total)
    this.balanceUsdtSubscription = new Subscription();
    const updateUsdtBalance = async () => {
      try {
        const balanceObservable = await this.binanceWithdrawalService.getBalance();
        const balance: BalanceResponse = await firstValueFrom(balanceObservable);
        this.saldoUsdt = balance.availableBalance || 0;
      } catch (error) {
        this.saldoUsdt = 0;
      }
    };
    // Actualiza cada 30 segundos
    const usdtIntervalId = setInterval(async () => {
      const previousBalance = this.saldoUsdt;
      await updateUsdtBalance();

      if (previousBalance !== this.saldoUsdt && this.montoAConvertir > 0) {
        this.resetConversionValues();
      }
    }, 30000);
    // Actualiza al iniciar
    updateUsdtBalance();
    // Limpia el intervalo al destruir
    this.balanceUsdtSubscription.add({
      unsubscribe: () => clearInterval(usdtIntervalId),
    });

    // Suscripción al balance de CLP usando cartolaService (evita CORS)
    this.balanceClpSubscription = new Subscription();
    const updateClpBalance = async () => {
      try {
        const saldoCliente = await this.cartolaService.getSaldoCliente(userId);
        this.saldoClp = saldoCliente?.saldo || 0;
      } catch (error) {
        this.saldoClp = 0;
      }
    };
    // Actualiza cada 30 segundos
    const intervalId = setInterval(async () => {
      const previousBalance = this.saldoClp;
      await updateClpBalance();

      if (previousBalance !== this.saldoClp && this.montoAConvertir > 0) {
        this.resetConversionValues();
      }
    }, 30000);
    // Actualiza al iniciar
    updateClpBalance();
    // Limpia el intervalo al destruir
    this.balanceClpSubscription.add({
      unsubscribe: () => clearInterval(intervalId),
    });
  }

  calcularConversion(): void {
    if (!this.isOperatingHours) {
      this.montoConvertido = 0;
      this.errorMessage = this.operatingHoursMessage;
      return;
    }

    this.actualizarTramo();

    if (!this.montoAConvertir || !this.usdtToClpRate) {
      this.montoConvertido = 0;
      return;
    }

    // Realizar el cálculo siempre que haya un monto y una tasa (la del tramo
    // del monto; sin tramos, la base de siempre)
    this.montoConvertido = this.montoAConvertir * (this.tasaAplicada || this.usdtToClpRate);

    // Establecer mensajes de error sin bloquear el cálculo
    if (this.saldoUsdt === 0) {
      this.errorMessage = 'No tienes saldo USDT disponible para convertir';
    } else if (this.saldoUsdt < this.MIN_USDT_AMOUNT) {
      this.errorMessage = `Saldo insuficiente. Necesitas al menos ${this.MIN_USDT_AMOUNT} USDT para convertir`;
    } else if (this.montoAConvertir < this.MIN_USDT_AMOUNT) {
      this.errorMessage = `El monto mínimo es ${this.MIN_USDT_AMOUNT} USDT`;
    } else if (this.montoAConvertir > this.saldoUsdt) {
      this.errorMessage = 'El monto excede tu saldo disponible';
    } else {
      this.errorMessage = null;
    }

  }

  /** Tasa, «Desde X» y extra del tramo del monto. Sin tramos: la base, sin extras. */
  private actualizarTramo(): void {
    const base = Number(this.usdtToClpRate) || 0;
    if (!this.tramosVenta || !(base > 0)) {
      this.tasaAplicada = this.usdtToClpRate;
      this.desdeTasa = null;
      this.extraClp = 0;
      return;
    }
    const c = cotizarVentaClp({ data: this.tramosVenta, montoUsdt: Number(this.montoAConvertir) || 0, base });
    this.tasaAplicada = c.tasa > 0 ? c.tasa : this.usdtToClpRate;
    this.desdeTasa = c.desde;
    this.extraClp = c.extraClp;
  }

  // ── Escalera de tramos (presentación; la tasa y el envío no cambian) ──

  private escaleraCache: { data: EstadoTramosUsdtClpResponse | null; esc: EscalonVista[] | null } | null = null;

  /**
   * Los tramos USDT→CLP en la forma de `app-escalera-tramos`. Null (la
   * pantalla queda como antes) si la llamada falló, con un solo tramo o si
   * todos pagan lo mismo.
   */
  get escaleraUsdtClp(): EscalonVista[] | null {
    const data = this.tramosVenta;
    if (!this.escaleraCache || this.escaleraCache.data !== data) {
      const esc = data
        ? escaleraDesde(
            tramosValidosClp(data).map((t) => ({ desde: Number(t.desde), tasa: Number(t.tasa) })),
            { unidadCorte: 'USDT', monedaMonto: 'USDT', tipoAhorro: 'RECIBE_MAS', exigirMejora: true },
          )
        : null;
      this.escaleraCache = { data, esc };
    }
    return this.escaleraCache.esc;
  }

  /** «Usar X» de la escalera: escribe los USDT en el campo, como si los tecleara. */
  usarMontoUsdt(monto: number): void {
    if (!this.isOperatingHours || !(monto > 0)) return;
    this.montoAConvertir = monto;
    this.calcularConversion();
  }

  get isValidAmount(): boolean {
    const baseValid =
      this.isOperatingHours &&
      this.saldoUsdt >= this.MIN_USDT_AMOUNT &&
      this.montoAConvertir >= this.MIN_USDT_AMOUNT &&
      this.montoAConvertir <= this.saldoUsdt;

    if (this.isVeUser) {
      return baseValid && this.selectedBeneficiary !== null;
    }
    return baseValid;
  }

  async realizarCompra(): Promise<void> {
    if (!this.isOperatingHours) {
      this.errorMessage = this.operatingHoursMessage;
      return;
    }

    if (!this.isValidAmount || this.isConverting || !this.usdtToClpRate) {
      return;
    }

    if (this.isVeUser && !this.selectedBeneficiary) {
      this.errorMessage = 'Debes seleccionar un beneficiario';
      return;
    }

    this.isConverting = true;
    this.errorMessage = null;

    try {
      if (this.isVeUser) {
        await this.realizarRetiroVe();
      } else {
        await this.realizarConversionClp();
      }
    } catch (error: any) {
      await this.manejarErrorEnvio(error);
    } finally {
      this.isConverting = false;
    }
  }

  /**
   * Error de la venta o del retiro VE, con su status real (`errorSwapUsdtVes`
   * → `errorHttpReal`). Ver `clasificarErrorConversionClp`.
   */
  async manejarErrorEnvio(error: any): Promise<void> {
    const tipo = clasificarErrorConversionClp(error);
    if (tipo === 'TASA_CAMBIO') {
      // No se hizo nada: se recotiza con la base y los tramos nuevos.
      await this.recotizar();
      this.errorMessage = TEXTO_TASA_CAMBIO_CLP;
      return;
    }
    if (tipo === 'SIN_TASA') {
      this.errorMessage = TEXTO_SIN_TASA_CLP;
      return;
    }
    if (tipo === 'INCIERTO') {
      // Pudo haberse hecho: no se ofrece reintentar desde aquí.
      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'warning',
        title: 'No pudimos confirmar la operación',
        text: mensajeEnvioIncierto(this.isVeUser ? 'withdraw' : 'wallet'),
        confirmButtonText: 'Entendido',
      });
      this.router.navigate(['/wallet/client/load-pays-usdt']);
      return;
    }
    this.errorMessage = error?.message || 'Error al procesar la conversión';
  }

  private async realizarConversionClp(): Promise<void> {
    const conversionData = {
      userId: this.user.usuarioId,
      usdtAmount: this.montoAConvertir,
      clpRate: this.tasaAplicada || this.usdtToClpRate!,
      clpAmount: this.montoConvertido,
    };

    const response = await firstValueFrom(
      this.binanceDepositService.convertUsdtToClpConErrorReal(conversionData)
    );

    if (response.success) {
      // Lo que ACREDITÓ el servidor (puede liquidar con su tasa), no lo del
      // formulario. Sin esos campos (backend viejo), el mensaje de siempre.
      const c: any = (response as any)?.conversion;
      const clp = Number(c?.clpAmount);
      const tasa = Number(c?.rate);
      const usdt = Number(c?.usdtAmount);
      const conMontos = Number.isFinite(clp) && clp > 0 && Number.isFinite(tasa) && tasa > 0;
      await Swal.fire({
        icon: 'success',
        title: '¡Conversión exitosa!',
        ...(conMontos
          ? {
              html:
                `<p>${this.escaparHtml(response.message || '')}</p>` +
                `<p style="margin-top: 10px;"><strong>Recibiste:</strong> ${this.formatCLP(clp)} CLP` +
                (Number.isFinite(usdt) && usdt > 0 ? ` por ${this.formatUSDT(usdt)} USDT` : '') +
                `</p><p style="margin-top: 4px; font-size: 0.9em; color: #374151;">Tasa: ${this.formatCLP(tasa)} CLP por USDT</p>`,
            }
          : { text: response.message }),
        timer: 5000,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      this.router.navigate(['/wallet/client/load-pays-usdt']);
    }
  }

  private async realizarRetiroVe(): Promise<void> {
    const retiroData: any = {
      userId: String(this.user.usuarioId),
      usdtAmount: this.montoAConvertir,
      clpAmount: this.montoConvertido,
      tasa: this.tasaAplicada || this.usdtToClpRate!,
      cuentaRetiroId: this.selectedBeneficiary!.id,
    };

    // Campos opcionales si están disponibles en la tasa
    if (this.rateValues?.tasaCambioId) {
      retiroData.tasaCambioId = this.rateValues.tasaCambioId;
    }
    if (this.rateValues?.valorCambioId) {
      retiroData.valorCambioId = this.rateValues.valorCambioId;
    }

    const response = await firstValueFrom(
      this.binanceDepositService.retiroUsdtClpVesConErrorReal(retiroData)
    );

    await Swal.fire({
      confirmButtonColor: '#1e5a3f',
      icon: 'success',
      title: 'Retiro creado',
      text: response.message || 'Retiro USDT→CLP creado exitosamente. Pendiente de aprobación.',
      confirmButtonText: 'Entendido',
    });
    this.router.navigate(['/wallet/client/load-pays-usdt']);
  }

  get validationMessage(): string | null {
    if (!this.isOperatingHours) {
      return this.operatingHoursMessage;
    }
    if (this.saldoUsdt === 0) {
      return 'No tienes saldo USDT disponible para convertir';
    }
    if (this.saldoUsdt < this.MIN_USDT_AMOUNT) {
      return `Saldo insuficiente. Necesitas al menos ${this.MIN_USDT_AMOUNT} USDT para convertir`;
    }
    if (this.montoAConvertir < this.MIN_USDT_AMOUNT) {
      return `El monto mínimo es ${this.MIN_USDT_AMOUNT} USDT`;
    }
    if (this.montoAConvertir > this.saldoUsdt) {
      return 'El monto excede tu saldo disponible';
    }
    if (this.isVeUser && this.montoAConvertir >= this.MIN_USDT_AMOUNT && !this.selectedBeneficiary) {
      return 'Selecciona un beneficiario para continuar';
    }
    return null;
  }

  get showComprarButton(): boolean {
    return (
      this.isValidAmount &&
      this.montoConvertido > 0 &&
      !this.errorMessage &&
      !this.isConverting
    );
  }

  // ============================================================
  // BENEFICIARY METHODS (solo VE)
  // ============================================================

  async loadBeneficiaries(): Promise<void> {
    this.loadingBeneficiarios = true;
    try {
      this.beneficiaries = await this.withdrawAccountService.getWithdrawAccounts();
    } catch (error) {
      this.beneficiaries = [];
    } finally {
      this.loadingBeneficiarios = false;
    }
  }

  get filteredBeneficiaries(): CuentaRetiro[] {
    if (!this.searchTerm) return this.beneficiaries;
    const term = this.searchTerm.toLowerCase();
    return this.beneficiaries.filter(
      (b) =>
        b.alias?.toLowerCase().includes(term) ||
        b.banco?.toLowerCase().includes(term) ||
        b.numeroCuenta?.toLowerCase().includes(term)
    );
  }

  selectBeneficiary(cuenta: CuentaRetiro): void {
    this.selectedBeneficiary = cuenta;
    if (this.errorMessage === 'Debes seleccionar un beneficiario') {
      this.errorMessage = null;
    }
    this.calcularConversion();
  }

  openAddBeneficiaryDialog(): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(AddWithdrawAccountsComponent, {
      data: {
        type: 'CREATE',
        cuenta: null,
        preselectedCountry: 'CL',
      },
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        await this.loadBeneficiaries();
      }
    });
  }

  resetConversionValues(): void {
    this.montoAConvertir = 0;
    this.montoConvertido = 0;
    this.errorMessage = null;
    this.rateChanged = false;
  }

  navigateBack(): void {
    this.router.navigate(['/wallet/client/load-pays-usdt']);
  }

  formatCLP(value: number): string {
    if (!value && value !== 0) return '$ 0';
    // Redondear hacia abajo con máximo 2 decimales
    const roundedValue = Math.floor(value * 100) / 100;
    try {
      return (
        this.currencyPipe.transform(
          roundedValue,
          'CLP',
          'symbol-narrow',
          '1.0-2',
          'es-CL'
        ) ||
        `$ ${roundedValue.toLocaleString('es-CL', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`
      );
    } catch (error) {
      return `$ ${roundedValue.toLocaleString('es-CL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    }
  }

  /** El mensaje del servidor va dentro de `html`: sin marcas. */
  private escaparHtml(t: string): string {
    return String(t).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
  }

  formatUSDT(value: number): string {
    if (!value && value !== 0) return '0.00';
    // Redondear hacia abajo con máximo 2 decimales
    const roundedValue = Math.floor(value * 100) / 100;
    return roundedValue.toFixed(2);
  }

  formatUSDTWithSymbol(value: number): string {
    if (!value && value !== 0) return '$0.00 USDT';
    // Redondear hacia abajo con máximo 2 decimales
    const roundedValue = Math.floor(value * 100) / 100;
    return `$${roundedValue.toFixed(2)} USDT`;
  }

  get processingMessage(): string {
    return this.isConverting ? 'Procesando...' : 'Convertir';
  }
}
